import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { profileService } from '../services/supabaseService';
import { storageService } from '../services/storageService';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User as UserIcon, 
  Crown, 
  CheckCircle2, 
  ArrowRight, 
  ArrowLeft, 
  Camera, 
  MapPin, 
  Heart, 
  Settings, 
  Bell, 
  ShieldCheck,
  Loader2,
  Lock,
  Search,
  Zap,
  Info,
  Globe,
  Star,
  DollarSign,
  Clock,
  ThumbsUp,
  ThumbsDown,
  Eye,
  EyeOff,
  Upload as UploadIcon,
  Sparkles,
  Coffee,
  Smile
} from 'lucide-react';
import { 
  GENDER_OPTIONS, 
  USER_INTERESTS, 
  HIRE_A_FRIEND_SERVICES, 
  FWB_SERVICES,
  HOBBIES,
  PERSONALITY_TRAITS,
  LOOKING_FOR_OPTIONS
} from '../constants/onboarding';

const OnboardingPage = () => {
  const { user, userProfile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [step, setStep] = useState(0); 
  const [role, setRole] = useState(null); 
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(true);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [error, setError] = useState('');

  // Form State
  const [form, setForm] = useState({
    username: '',
    displayName: '',
    avatarUrl: '',
    birthday: '',
    gender: '',
    city: '',
    bio: '',
    languages: ['English', 'Hindi'],
    interests: [],
    hobbies: [],
    personalityTraits: [],
    preferences: {
      lookingFor: [], 
      relationshipStyle: '',
      gender: 'any',
      ageRange: [18, 60]
    },
    privacy: {
      hideProfile: false,
      blurPhoto: false,
      allowDms: true,
      anonymousMode: true
    },
    notifications: {
      push: true,
      email: true
    },
    // Provider Specific
    kyc: {
      aadhaar: null,
      selfie: null,
      status: 'pending'
    },
    services: [],
    fwbServices: [],
    pricing: {
      hourly: 500,
      session: 1500,
      customToggle: false
    },
    availability: {
      days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
      slots: ['7 PM - 1 AM'],
      onlineStatus: true
    },
    dos: 'Respectful conversations only',
    donts: 'No abusive language'
  });

  useEffect(() => {
    if (userProfile?.onboarding_completed_at) {
      navigate('/dashboard', { replace: true });
    }
    if (userProfile?.role) {
      setRole(userProfile.role === 'service_provider' ? 'provider' : 'user');
      setStep(1);
    }
  }, [userProfile, navigate]);

  // Username Availability Check
  useEffect(() => {
    if (!form.username || form.username.length < 3) {
      setUsernameAvailable(true);
      return;
    }

    const timer = setTimeout(async () => {
      setCheckingUsername(true);
      try {
        const available = await profileService.checkUsernameAvailability(form.username, user.id);
        setUsernameAvailable(available);
      } catch (err) {
        console.error(err);
      } finally {
        setCheckingUsername(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [form.username, user.id]);

  const updateForm = (updates) => setForm(p => ({ ...p, ...updates }));
  const handleNext = () => setStep(s => s + 1);
  const handleBack = () => setStep(s => s - 1);

  const toggleItem = (list, item) => {
    return list.includes(item) ? list.filter(i => i !== item) : [...list, item];
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError('');
    try {
      const publicUrl = await storageService.uploadAvatar(user.id, file);
      updateForm({ avatarUrl: publicUrl });
    } catch (err) {
      setError(err.message || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleFinish = async () => {
    setError('');
    setSaving(true);
    try {
      // Map form data to existing database columns to avoid 'missing column' errors
      const payload = {
        username: form.username,
        display_name: form.displayName,
        avatar_url: form.avatarUrl,
        birthday: form.birthday,
        gender: form.gender,
        city: form.city,
        bio: form.bio,
        email: user.email,
        role: role === 'provider' ? 'service_provider' : 'user',
        interested_in: [...form.interests, ...form.hobbies],
        // Pack extra data into personality_traits to avoid schema mismatch
        personality_traits: [
          ...form.personalityTraits,
          JSON.stringify({
            languages: form.languages,
            privacy: form.privacy,
            notifications: form.notifications,
            pricing: form.pricing,
            availability: form.availability,
            rules: { dos: form.dos, donts: form.donts },
            preferences: form.preferences,
            detailed_traits: form.personalityTraits
          })
        ],
        sexual_preferences: role === 'provider' ? [...form.services, ...form.fwbServices] : (form.preferences.lookingFor || []),
        onboarding_completed_at: new Date().toISOString()
      };
      
      await profileService.setOnboardingCompleted(user.id, payload);
      await refreshProfile();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      if (err.message?.includes('user_profiles_username_key')) {
        setError('This username is already taken. Please go back to Step 1 and choose another.');
      } else {
        setError(err.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const stepVariants = {
    initial: { x: 20, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: -20, opacity: 0 }
  };

  if (!userProfile) return null;

  const totalSteps = role === 'provider' ? 11 : 9;

  return (
    <div className="min-h-screen bg-[#0f172a] text-slate-200 flex items-center justify-center p-4 font-sans overflow-x-hidden">
      {/* Background Glow */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-brand-pink/10 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-500/10 blur-[120px] rounded-full" />
      </div>

      <div className="w-full max-w-xl relative z-10 py-10">
        <AnimatePresence mode="wait">
          {/* STEP 0: Role Selection */}
          {step === 0 && (
            <motion.div key="step0" variants={stepVariants} initial="initial" animate="animate" exit="exit" className="space-y-8">
              <div className="text-center">
                <h1 className="text-4xl font-black text-white mb-2">Choose Your Role</h1>
                <p className="text-slate-400">How do you want to use Stranger69?</p>
              </div>
              <div className="grid gap-4">
                <button onClick={() => { setRole('user'); setStep(1); }} className="group relative p-6 rounded-3xl bg-slate-800/50 border border-white/5 hover:border-brand-pink/50 transition-all text-left overflow-hidden">
                   <div className="relative flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-brand-pink/10 border border-brand-pink/20 flex items-center justify-center text-brand-pink group-hover:scale-110 transition-transform"><UserIcon size={24} /></div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">Regular User</h3>
                      <p className="text-sm text-slate-400 leading-relaxed">Browse verified friends, chat anonymously, and find meaningful connections.</p>
                      <div className="mt-4 flex items-center gap-2 text-brand-pink text-sm font-bold">Continue as User <ArrowRight size={16} /></div>
                    </div>
                  </div>
                </button>
                <button onClick={() => { setRole('provider'); setStep(1); }} className="group relative p-6 rounded-3xl bg-slate-800/50 border border-white/5 hover:border-amber-500/50 transition-all text-left overflow-hidden">
                   <div className="relative flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-500 group-hover:scale-110 transition-transform"><Crown size={24} /></div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">Service Provider</h3>
                      <p className="text-sm text-slate-400 leading-relaxed">Create a profile, offer companionship services, and earn while you connect.</p>
                      <div className="mt-4 flex items-center gap-2 text-amber-500 text-sm font-bold">Continue as Provider <ArrowRight size={16} /></div>
                    </div>
                  </div>
                </button>
              </div>
            </motion.div>
          )}

          {/* STEP 1: Identity */}
          {step === 1 && (
            <motion.div key="step1" variants={stepVariants} initial="initial" animate="animate" exit="exit" className="bg-slate-900/50 backdrop-blur-2xl border border-white/5 p-8 rounded-[40px] shadow-2xl">
              <div className="mb-8">
                <div className="flex justify-between items-center mb-6">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Step 1 of {totalSteps}</span>
                  <div className="flex gap-1">{[...Array(totalSteps)].map((_, i) => <div key={i} className={`h-1 rounded-full transition-all ${i < step ? 'w-4 bg-brand-pink' : 'w-2 bg-white/10'}`} />)}</div>
                </div>
                <h2 className="text-2xl font-black text-white">{role === 'provider' ? 'Provider Identity' : 'Create Identity'}</h2>
                <p className="text-slate-400 text-sm">How should we identify you?</p>
              </div>
              <div className="space-y-6">
                {error && <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-bold">{error}</div>}
                
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Username / Alias</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-bold">@</span>
                    <input type="text" value={form.username} onChange={e => updateForm({ username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })} className={`w-full pl-10 pr-10 py-4 rounded-2xl bg-slate-800 border focus:border-brand-pink/50 text-white font-bold transition-all ${!usernameAvailable ? 'border-red-500/50' : 'border-white/5'}`} placeholder="night_owl" />
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center">
                      {checkingUsername ? (
                        <Loader2 className="animate-spin text-slate-500" size={16} />
                      ) : form.username.length >= 3 && (
                        usernameAvailable ? (
                          <CheckCircle2 className="text-green-500" size={16} />
                        ) : (
                          <Lock className="text-red-500" size={16} />
                        )
                      )}
                    </div>
                  </div>
                  {form.username.length >= 3 && !usernameAvailable && !checkingUsername && (
                    <p className="text-[10px] text-red-400 font-bold mt-2 ml-1">This username is already taken. Please try another.</p>
                  )}
                </div>
                <div className="flex gap-4">
                   <div className="flex-1">
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Profile Photo</label>
                      <input 
                        type="file" 
                        ref={fileInputRef} 
                        onChange={handleFileChange} 
                        accept="image/*" 
                        className="hidden" 
                      />
                      <button 
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="w-full aspect-square rounded-3xl bg-slate-800 border-2 border-dashed border-white/10 flex flex-col items-center justify-center gap-2 text-slate-500 hover:text-white hover:border-brand-pink/50 transition-all overflow-hidden relative"
                      >
                        {uploading ? (
                          <Loader2 className="animate-spin text-brand-pink" size={32} />
                        ) : form.avatarUrl ? (
                          <img src={form.avatarUrl} className="w-full h-full object-cover" alt="Preview" />
                        ) : (
                          <>
                            <Camera size={24} />
                            <span className="text-[10px] font-bold">Upload</span>
                          </>
                        )}
                        {form.avatarUrl && !uploading && (
                          <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 flex items-center justify-center transition-opacity">
                             <UploadIcon size={24} className="text-white" />
                          </div>
                        )}
                      </button>
                   </div>
                   <div className="flex-[2]">
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Display Name</label>
                      <input type="text" value={form.displayName} onChange={e => updateForm({ displayName: e.target.value })} className="w-full px-4 py-4 rounded-2xl bg-slate-800 border border-white/5 focus:border-brand-pink/50 text-white font-bold" placeholder="Amitava" />
                   </div>
                </div>
                <div className="flex gap-4 pt-4">
                  <button onClick={handleBack} className="p-4 rounded-2xl bg-white/5 border border-white/5 text-slate-400 hover:text-white"><ArrowLeft size={24} /></button>
                  <button onClick={handleNext} disabled={!form.username || !form.displayName || !form.avatarUrl || !usernameAvailable || checkingUsername} className="flex-1 py-4 rounded-2xl bg-brand-pink text-white font-black hover:bg-brand-pink-dark disabled:opacity-50 shadow-xl shadow-brand-pink/20">Continue</button>
                </div>
              </div>
            </motion.div>
          )}

          {/* STEP 2: KYC (Provider Only) or Basic Details (User) */}
          {step === 2 && (
            <motion.div key="step2" variants={stepVariants} initial="initial" animate="animate" exit="exit" className="bg-slate-900/50 backdrop-blur-2xl border border-white/5 p-8 rounded-[40px] shadow-2xl">
              <div className="mb-8">
                <div className="flex justify-between items-center mb-6">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Step 2 of {totalSteps}</span>
                  <div className="flex gap-1">{[...Array(totalSteps)].map((_, i) => <div key={i} className={`h-1 rounded-full transition-all ${i < step ? 'w-4 bg-brand-pink' : 'w-2 bg-white/10'}`} />)}</div>
                </div>
                <h2 className="text-2xl font-black text-white">{role === 'provider' ? 'KYC Verification' : 'Basic Profile'}</h2>
                <p className="text-slate-400 text-sm">{role === 'provider' ? 'Mandatory for platform safety.' : 'Tell us a bit more about yourself.'}</p>
              </div>

              {role === 'provider' ? (
                <div className="space-y-6">
                   <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-200 text-xs flex gap-3"><Info size={16} className="shrink-0" /> Provider profile stays hidden until KYC is approved.</div>
                   <div className="grid grid-cols-2 gap-4">
                      <button className="p-6 rounded-3xl bg-slate-800 border border-white/5 flex flex-col items-center gap-3 text-slate-400 hover:text-white hover:border-brand-pink/50 transition-all"><ShieldCheck size={32} /><span className="text-xs font-bold text-center">Aadhaar / ID Card</span></button>
                      <button className="p-6 rounded-3xl bg-slate-800 border border-white/5 flex flex-col items-center gap-3 text-slate-400 hover:text-white hover:border-brand-pink/50 transition-all"><Camera size={32} /><span className="text-xs font-bold text-center">Selfie Verification</span></button>
                   </div>
                   <div className="flex gap-4 pt-4">
                      <button onClick={handleBack} className="p-4 rounded-2xl bg-white/5 border border-white/5 text-slate-400 hover:text-white"><ArrowLeft size={24} /></button>
                      <button onClick={handleNext} className="flex-1 py-4 rounded-2xl bg-brand-pink text-white font-black hover:bg-brand-pink-dark shadow-xl shadow-brand-pink/20">Submit for Review</button>
                   </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Birthday</label><input type="date" value={form.birthday} onChange={e => updateForm({ birthday: e.target.value })} className="w-full px-4 py-4 rounded-2xl bg-slate-800 border border-white/5 text-white font-bold" /></div>
                    <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Gender</label><select value={form.gender} onChange={e => updateForm({ gender: e.target.value })} className="w-full px-4 py-4 rounded-2xl bg-slate-800 border border-white/5 text-white font-bold"><option value="">Select</option>{GENDER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
                  </div>
                  <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">City / State</label><div className="relative"><MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={20} /><input type="text" value={form.city} onChange={e => updateForm({ city: e.target.value })} className="w-full pl-12 pr-4 py-4 rounded-2xl bg-slate-800 border border-white/5 text-white font-bold" placeholder="Mumbai, Maharashtra" /></div></div>
                  <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Short Bio</label><textarea value={form.bio} onChange={e => updateForm({ bio: e.target.value.slice(0, 150) })} className="w-full px-4 py-4 rounded-2xl bg-slate-800 border border-white/5 text-white font-bold resize-none h-24" placeholder="Just here for meaningful conversations ✨" /><div className="text-right text-[10px] font-bold text-slate-500 mt-1">{form.bio.length}/150</div></div>
                  <div className="flex gap-4 pt-4">
                    <button onClick={handleBack} className="p-4 rounded-2xl bg-white/5 border border-white/5 text-slate-400 hover:text-white"><ArrowLeft size={24} /></button>
                    <button onClick={handleNext} disabled={!form.birthday || !form.gender || !form.city || !form.bio} className="flex-1 py-4 rounded-2xl bg-brand-pink text-white font-black hover:bg-brand-pink-dark disabled:opacity-50 shadow-xl shadow-brand-pink/20">Continue</button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* STEP 3: Interests (User) or Category Selection (Provider) */}
          {step === 3 && (
            <motion.div key="step3" variants={stepVariants} initial="initial" animate="animate" exit="exit" className="bg-slate-900/50 backdrop-blur-2xl border border-white/5 p-8 rounded-[40px] shadow-2xl">
              <div className="mb-8">
                <div className="flex justify-between items-center mb-6">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Step 3 of {totalSteps}</span>
                  <div className="flex gap-1">{[...Array(totalSteps)].map((_, i) => <div key={i} className={`h-1 rounded-full transition-all ${i < step ? 'w-4 bg-brand-pink' : 'w-2 bg-white/10'}`} />)}</div>
                </div>
                <h2 className="text-2xl font-black text-white">{role === 'provider' ? 'Service Categories' : 'Your Interests'}</h2>
                <p className="text-slate-400 text-sm">{role === 'provider' ? 'What kind of connections do you offer?' : 'Select at least 3 things you enjoy.'}</p>
              </div>

              <div className="space-y-6">
                {role === 'provider' ? (
                  <div className="grid gap-3">
                    {[
                      { id: 'hire_a_friend', label: 'Hire a Friend', icon: UserIcon },
                      { id: 'fwb', label: 'FWB (18+)', icon: Zap }
                    ].map(o => (
                      <button key={o.id} onClick={() => updateForm({ preferences: { ...form.preferences, lookingFor: toggleItem(form.preferences.lookingFor, o.id) } })} className={`flex items-center gap-4 p-6 rounded-3xl border transition-all ${form.preferences.lookingFor.includes(o.id) ? 'bg-brand-pink/10 border-brand-pink text-white' : 'bg-slate-800 border-white/5 text-slate-400'}`}>
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${form.preferences.lookingFor.includes(o.id) ? 'bg-brand-pink text-white' : 'bg-slate-900'}`}><o.icon size={24} /></div>
                        <span className="text-lg font-bold">{o.label}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {USER_INTERESTS.map(item => {
                      const active = form.interests.includes(item.value);
                      return (<button key={item.value} onClick={() => updateForm({ interests: toggleItem(form.interests, item.value) })} className={`px-4 py-2.5 rounded-2xl border text-sm font-bold transition-all flex items-center gap-2 ${active ? 'bg-brand-pink text-white border-brand-pink shadow-lg shadow-brand-pink/20' : 'bg-slate-800 border-white/5 text-slate-400 hover:border-white/20'}`}><span>{item.emoji}</span>{item.label}</button>);
                    })}
                  </div>
                )}
                <div className="flex gap-4 pt-4">
                  <button onClick={handleBack} className="p-4 rounded-2xl bg-white/5 border border-white/5 text-slate-400 hover:text-white"><ArrowLeft size={24} /></button>
                  <button onClick={handleNext} disabled={role === 'provider' ? form.preferences.lookingFor.length === 0 : form.interests.length < 3} className="flex-1 py-4 rounded-2xl bg-brand-pink text-white font-black hover:bg-brand-pink-dark disabled:opacity-50 shadow-xl shadow-brand-pink/20">Continue</button>
                </div>
              </div>
            </motion.div>
          )}

          {/* STEP 4: Hobbies & Personality (User Only) */}
          {step === 4 && role === 'user' && (
            <motion.div key="step4" variants={stepVariants} initial="initial" animate="animate" exit="exit" className="bg-slate-900/50 backdrop-blur-2xl border border-white/5 p-8 rounded-[40px] shadow-2xl">
              <div className="mb-8">
                <div className="flex justify-between items-center mb-6">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Step 4 of {totalSteps}</span>
                  <div className="flex gap-1">{[...Array(totalSteps)].map((_, i) => <div key={i} className={`h-1 rounded-full transition-all ${i < step ? 'w-4 bg-brand-pink' : 'w-2 bg-white/10'}`} />)}</div>
                </div>
                <h2 className="text-2xl font-black text-white">Traits & Hobbies</h2>
                <p className="text-slate-400 text-sm">Tell us more about your personality.</p>
              </div>

              <div className="space-y-6">
                <div>
                   <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Personality Traits</label>
                   <div className="flex flex-wrap gap-2">
                     {PERSONALITY_TRAITS.map(item => (
                       <button key={item.value} onClick={() => updateForm({ personalityTraits: toggleItem(form.personalityTraits, item.value) })} className={`px-4 py-2 rounded-xl border text-xs font-bold transition-all ${form.personalityTraits.includes(item.value) ? 'bg-indigo-500 text-white border-indigo-500' : 'bg-slate-800 border-white/5 text-slate-400'}`}><span>{item.emoji}</span> {item.label}</button>
                     ))}
                   </div>
                </div>
                <div>
                   <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Your Hobbies</label>
                   <div className="flex flex-wrap gap-2">
                     {HOBBIES.map(item => (
                       <button key={item.value} onClick={() => updateForm({ hobbies: toggleItem(form.hobbies, item.value) })} className={`px-4 py-2 rounded-xl border text-xs font-bold transition-all ${form.hobbies.includes(item.value) ? 'bg-brand-pink text-white border-brand-pink' : 'bg-slate-800 border-white/5 text-slate-400'}`}><span>{item.emoji}</span> {item.label}</button>
                     ))}
                   </div>
                </div>
                <div className="flex gap-4 pt-4">
                  <button onClick={handleBack} className="p-4 rounded-2xl bg-white/5 border border-white/5 text-slate-400 hover:text-white"><ArrowLeft size={24} /></button>
                  <button onClick={handleNext} disabled={form.personalityTraits.length < 2 || form.hobbies.length < 2} className="flex-1 py-4 rounded-2xl bg-brand-pink text-white font-black hover:bg-brand-pink-dark shadow-xl shadow-brand-pink/20">Continue</button>
                </div>
              </div>
            </motion.div>
          )}

          {/* STEP 5: Looking For (User Only) */}
          {step === 5 && role === 'user' && (
            <motion.div key="step5" variants={stepVariants} initial="initial" animate="animate" exit="exit" className="bg-slate-900/50 backdrop-blur-2xl border border-white/5 p-8 rounded-[40px] shadow-2xl">
              <div className="mb-8">
                <div className="flex justify-between items-center mb-6">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Step 5 of {totalSteps}</span>
                  <div className="flex gap-1">{[...Array(totalSteps)].map((_, i) => <div key={i} className={`h-1 rounded-full transition-all ${i < step ? 'w-4 bg-brand-pink' : 'w-2 bg-white/10'}`} />)}</div>
                </div>
                <h2 className="text-2xl font-black text-white">Looking For</h2>
                <p className="text-slate-400 text-sm">What's your goal on Stranger69?</p>
              </div>

              <div className="space-y-6">
                 <div className="grid gap-3">
                   {LOOKING_FOR_OPTIONS.map(item => (
                     <button key={item.value} onClick={() => updateForm({ preferences: { ...form.preferences, relationshipStyle: item.value } })} className={`flex items-center gap-4 p-5 rounded-3xl border transition-all ${form.preferences.relationshipStyle === item.value ? 'bg-brand-pink/10 border-brand-pink text-white' : 'bg-slate-800 border-white/5 text-slate-400'}`}>
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl ${form.preferences.relationshipStyle === item.value ? 'bg-brand-pink text-white' : 'bg-slate-900'}`}>{item.emoji}</div>
                        <span className="font-bold">{item.label}</span>
                     </button>
                   ))}
                 </div>
                 <div className="flex gap-4 pt-4">
                  <button onClick={handleBack} className="p-4 rounded-2xl bg-white/5 border border-white/5 text-slate-400 hover:text-white"><ArrowLeft size={24} /></button>
                  <button onClick={handleNext} disabled={!form.preferences.relationshipStyle} className="flex-1 py-4 rounded-2xl bg-brand-pink text-white font-black hover:bg-brand-pink-dark shadow-xl shadow-brand-pink/20">Continue</button>
                </div>
              </div>
            </motion.div>
          )}

          {/* PROVIDER FLOW STEP 4: About Me Setup */}
          {step === 4 && role === 'provider' && (
             <motion.div key="step4p" variants={stepVariants} initial="initial" animate="animate" exit="exit" className="bg-slate-900/50 backdrop-blur-2xl border border-white/5 p-8 rounded-[40px] shadow-2xl">
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-6"><span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Step 4 of 11</span><div className="flex gap-1">{[...Array(11)].map((_, i) => <div key={i} className={`h-1 rounded-full transition-all ${i < step ? 'w-4 bg-brand-pink' : 'w-2 bg-white/10'}`} />)}</div></div>
                  <h2 className="text-2xl font-black text-white">About Me Setup</h2>
                  <p className="text-slate-400 text-sm">Detailed bio for your clients.</p>
                </div>
                <div className="space-y-6">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Detailed Bio</label>
                    <textarea value={form.bio} onChange={e => updateForm({ bio: e.target.value.slice(0, 500) })} className="w-full px-4 py-4 rounded-2xl bg-slate-800 border border-white/5 text-white font-bold h-40" placeholder="Friendly, open-minded and love deep conversations..." />
                    <div className="text-right text-[10px] font-bold text-slate-500 mt-1">{form.bio.length}/500</div>
                  </div>
                  <div className="flex gap-4 pt-4">
                    <button onClick={handleBack} className="p-4 rounded-2xl bg-white/5 border border-white/5 text-slate-400 hover:text-white"><ArrowLeft size={24} /></button>
                    <button onClick={handleNext} disabled={form.bio.length < 20} className="flex-1 py-4 rounded-2xl bg-brand-pink text-white font-black hover:bg-brand-pink-dark disabled:opacity-50 shadow-xl shadow-brand-pink/20">Continue</button>
                  </div>
                </div>
             </motion.div>
          )}

          {/* PROVIDER FLOW STEP 5: Services Offered */}
          {step === 5 && role === 'provider' && (
             <motion.div key="step5p" variants={stepVariants} initial="initial" animate="animate" exit="exit" className="bg-slate-900/50 backdrop-blur-2xl border border-white/5 p-8 rounded-[40px] shadow-2xl">
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-6"><span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Step 5 of 11</span><div className="flex gap-1">{[...Array(11)].map((_, i) => <div key={i} className={`h-1 rounded-full transition-all ${i < step ? 'w-4 bg-brand-pink' : 'w-2 bg-white/10'}`} />)}</div></div>
                  <h2 className="text-2xl font-black text-white">Services Offered</h2>
                  <p className="text-slate-400 text-sm">What can clients book you for?</p>
                </div>
                <div className="space-y-8 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                  {form.preferences.lookingFor.includes('hire_a_friend') && (
                    <div>
                      <h4 className="text-xs font-black uppercase tracking-widest text-brand-pink mb-4">Hire a Friend Services</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {HIRE_A_FRIEND_SERVICES.map(s => (
                          <button key={s.value} onClick={() => updateForm({ services: toggleItem(form.services, s.value) })} className={`px-4 py-2 rounded-xl border text-[10px] font-bold transition-all ${form.services.includes(s.value) ? 'bg-brand-pink text-white border-brand-pink shadow-lg shadow-brand-pink/10' : 'bg-slate-800 border-white/5 text-slate-400 hover:border-white/20'}`}>{s.label}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {form.preferences.lookingFor.includes('fwb') && (
                    <div>
                      <h4 className="text-xs font-black uppercase tracking-widest text-indigo-400 mb-4">FWB Services</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {FWB_SERVICES.map(s => (
                          <button key={s.value} onClick={() => updateForm({ fwbServices: toggleItem(form.fwbServices, s.value) })} className={`px-4 py-2 rounded-xl border text-[10px] font-bold transition-all ${form.fwbServices.includes(s.value) ? 'bg-indigo-500 text-white border-indigo-500 shadow-lg shadow-indigo-500/10' : 'bg-slate-800 border-white/5 text-slate-400 hover:border-white/20'}`}>{s.label}</button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-4 pt-8">
                  <button onClick={handleBack} className="p-4 rounded-2xl bg-white/5 border border-white/5 text-slate-400 hover:text-white"><ArrowLeft size={24} /></button>
                  <button onClick={handleNext} disabled={form.services.length === 0 && form.fwbServices.length === 0} className="flex-1 py-4 rounded-2xl bg-brand-pink text-white font-black hover:bg-brand-pink-dark disabled:opacity-50 shadow-xl shadow-brand-pink/20">Continue</button>
                </div>
             </motion.div>
          )}

          {/* PROVIDER FLOW STEP 6: Pricing Setup */}
          {step === 6 && role === 'provider' && (
             <motion.div key="step6p" variants={stepVariants} initial="initial" animate="animate" exit="exit" className="bg-slate-900/50 backdrop-blur-2xl border border-white/5 p-8 rounded-[40px] shadow-2xl">
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-6"><span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Step 6 of 11</span><div className="flex gap-1">{[...Array(11)].map((_, i) => <div key={i} className={`h-1 rounded-full transition-all ${i < step ? 'w-4 bg-brand-pink' : 'w-2 bg-white/10'}`} />)}</div></div>
                  <h2 className="text-2xl font-black text-white">Pricing Setup</h2>
                  <p className="text-slate-400 text-sm">How much do you charge for your time?</p>
                </div>
                <div className="space-y-6">
                  <div className="grid gap-4">
                    <div className="p-6 rounded-3xl bg-slate-800 border border-white/5 flex items-center justify-between shadow-inner">
                       <div className="flex items-center gap-4"><div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center text-brand-pink shadow-lg"><DollarSign size={20} /></div><span className="font-bold text-white">Price per Hour</span></div>
                       <input type="number" value={form.pricing.hourly} onChange={e => updateForm({ pricing: { ...form.pricing, hourly: parseInt(e.target.value) } })} className="w-24 bg-slate-900 border border-white/10 rounded-xl px-3 py-2 text-right font-black text-brand-pink focus:border-brand-pink/50 outline-none" />
                    </div>
                    <div className="p-6 rounded-3xl bg-slate-800 border border-white/5 flex items-center justify-between shadow-inner">
                       <div className="flex items-center gap-4"><div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center text-indigo-400 shadow-lg"><Star size={20} /></div><span className="font-bold text-white">Price per Session</span></div>
                       <input type="number" value={form.pricing.session} onChange={e => updateForm({ pricing: { ...form.pricing, session: parseInt(e.target.value) } })} className="w-24 bg-slate-900 border border-white/10 rounded-xl px-3 py-2 text-right font-black text-indigo-400 focus:border-indigo-400/50 outline-none" />
                    </div>
                  </div>
                  <div className="flex gap-4 pt-6">
                    <button onClick={handleBack} className="p-4 rounded-2xl bg-white/5 border border-white/5 text-slate-400 hover:text-white"><ArrowLeft size={24} /></button>
                    <button onClick={handleNext} className="flex-1 py-4 rounded-2xl bg-brand-pink text-white font-black hover:bg-brand-pink-dark shadow-xl shadow-brand-pink/20">Continue</button>
                  </div>
                </div>
             </motion.div>
          )}

          {/* PROVIDER FLOW STEP 7: Availability */}
          {step === 7 && role === 'provider' && (
             <motion.div key="step7p" variants={stepVariants} initial="initial" animate="animate" exit="exit" className="bg-slate-900/50 backdrop-blur-2xl border border-white/5 p-8 rounded-[40px] shadow-2xl">
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-6"><span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Step 7 of 11</span><div className="flex gap-1">{[...Array(11)].map((_, i) => <div key={i} className={`h-1 rounded-full transition-all ${i < step ? 'w-4 bg-brand-pink' : 'w-2 bg-white/10'}`} />)}</div></div>
                  <h2 className="text-2xl font-black text-white">Availability</h2>
                  <p className="text-slate-400 text-sm">When are you active online?</p>
                </div>
                <div className="space-y-6">
                   <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Available Days</label>
                      <div className="flex flex-wrap gap-2">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                          <button key={day} onClick={() => updateForm({ availability: { ...form.availability, days: toggleItem(form.availability.days, day) } })} className={`w-10 h-10 rounded-xl border text-[10px] font-black transition-all ${form.availability.days.includes(day) ? 'bg-brand-pink text-white border-brand-pink shadow-lg shadow-brand-pink/20' : 'bg-slate-800 border-white/5 text-slate-500 hover:border-white/20'}`}>{day}</button>
                        ))}
                      </div>
                   </div>
                   <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Common Time Slots</label>
                      <div className="grid grid-cols-2 gap-2">
                         {['Morning', 'Afternoon', 'Evening', '7 PM - 1 AM'].map(slot => (
                           <button key={slot} onClick={() => updateForm({ availability: { ...form.availability, slots: toggleItem(form.availability.slots, slot) } })} className={`py-3 rounded-xl border text-xs font-bold transition-all ${form.availability.slots.includes(slot) ? 'bg-brand-pink/10 border-brand-pink text-white shadow-lg shadow-brand-pink/5' : 'bg-slate-800 border-white/5 text-slate-500 hover:border-white/20'}`}>{slot}</button>
                         ))}
                      </div>
                   </div>
                   <div className="flex gap-4 pt-6">
                    <button onClick={handleBack} className="p-4 rounded-2xl bg-white/5 border border-white/5 text-slate-400 hover:text-white"><ArrowLeft size={24} /></button>
                    <button onClick={handleNext} disabled={form.availability.days.length === 0} className="flex-1 py-4 rounded-2xl bg-brand-pink text-white font-black hover:bg-brand-pink-dark shadow-xl shadow-brand-pink/20">Continue</button>
                  </div>
                </div>
             </motion.div>
          )}

          {/* PROVIDER FLOW STEP 8: Do's & Don'ts */}
          {step === 8 && role === 'provider' && (
             <motion.div key="step8p" variants={stepVariants} initial="initial" animate="animate" exit="exit" className="bg-slate-900/50 backdrop-blur-2xl border border-white/5 p-8 rounded-[40px] shadow-2xl">
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-6"><span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Step 8 of 11</span><div className="flex gap-1">{[...Array(11)].map((_, i) => <div key={i} className={`h-1 rounded-full transition-all ${i < step ? 'w-4 bg-brand-pink' : 'w-2 bg-white/10'}`} />)}</div></div>
                  <h2 className="text-2xl font-black text-white">Platform Rules</h2>
                  <p className="text-slate-400 text-sm">Set expectations for your clients.</p>
                </div>
                <div className="space-y-6">
                   <div>
                      <label className="flex items-center gap-2 text-xs font-bold text-green-500 uppercase tracking-wider mb-2"><ThumbsUp size={14} /> My Do's</label>
                      <textarea value={form.dos} onChange={e => updateForm({ dos: e.target.value })} className="w-full px-4 py-3 rounded-2xl bg-slate-800 border border-white/5 text-white font-bold h-24 focus:border-green-500/50 outline-none" placeholder="Respectful conversations only..." />
                   </div>
                   <div>
                      <label className="flex items-center gap-2 text-xs font-bold text-red-500 uppercase tracking-wider mb-2"><ThumbsDown size={14} /> My Don'ts</label>
                      <textarea value={form.donts} onChange={e => updateForm({ donts: e.target.value })} className="w-full px-4 py-3 rounded-2xl bg-slate-800 border border-white/5 text-white font-bold h-24 focus:border-red-500/50 outline-none" placeholder="No abusive language..." />
                   </div>
                   <div className="flex gap-4 pt-6">
                    <button onClick={handleBack} className="p-4 rounded-2xl bg-white/5 border border-white/5 text-slate-400 hover:text-white"><ArrowLeft size={24} /></button>
                    <button onClick={handleNext} className="flex-1 py-4 rounded-2xl bg-brand-pink text-white font-black hover:bg-brand-pink-dark shadow-xl shadow-brand-pink/20">Continue</button>
                  </div>
                </div>
             </motion.div>
          )}

          {/* SHARED STEPS: Privacy & Visibility */}
          {((step === 6 && role === 'user') || (step === 9 && role === 'provider')) && (
            <motion.div key="privacy" variants={stepVariants} initial="initial" animate="animate" exit="exit" className="bg-slate-900/50 backdrop-blur-2xl border border-white/5 p-8 rounded-[40px] shadow-2xl">
              <div className="mb-8">
                <div className="flex justify-between items-center mb-6"><span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Step {step} of {totalSteps}</span><div className="flex gap-1">{[...Array(totalSteps)].map((_, i) => <div key={i} className={`h-1 rounded-full transition-all ${i < step ? 'w-4 bg-brand-pink' : 'w-2 bg-white/10'}`} />)}</div></div>
                <h2 className="text-2xl font-black text-white">Privacy & Visibility</h2>
                <p className="text-slate-400 text-sm">Control how you appear on the platform.</p>
              </div>
              <div className="space-y-4">
                {[
                  { id: 'hideProfile', label: role === 'provider' ? 'Pause Profile' : 'Hide Profile', desc: 'Not visible in browse friends' },
                  { id: 'blurPhoto', label: 'Blur Photos', desc: 'Photos blurred until you accept chat' },
                  { id: 'anonymousMode', label: 'Anonymous Mode', desc: 'Stay hidden from search engines' }
                ].map(o => (
                  <button key={o.id} onClick={() => updateForm({ privacy: { ...form.privacy, [o.id]: !form.privacy[o.id] } })} className="w-full flex items-center justify-between p-4 rounded-2xl bg-slate-800 border border-white/5 hover:border-white/20 transition-all text-left">
                    <div><h4 className="font-bold text-white text-sm">{o.label}</h4><p className="text-[10px] text-slate-500 font-medium uppercase tracking-tighter mt-1">{o.desc}</p></div>
                    <div className={`w-12 h-6 rounded-full transition-all relative ${form.privacy[o.id] ? 'bg-brand-pink' : 'bg-slate-700'}`}><div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${form.privacy[o.id] ? 'left-7' : 'left-1'}`} /></div>
                  </button>
                ))}
                <div className="flex gap-4 pt-6">
                  <button onClick={handleBack} className="p-4 rounded-2xl bg-white/5 border border-white/5 text-slate-400 hover:text-white"><ArrowLeft size={24} /></button>
                  <button onClick={handleNext} className="flex-1 py-4 rounded-2xl bg-brand-pink text-white font-black hover:bg-brand-pink-dark shadow-xl shadow-brand-pink/20">Continue</button>
                </div>
              </div>
            </motion.div>
          )}

          {/* SHARED STEPS: Notifications */}
          {((step === 7 && role === 'user') || (step === 10 && role === 'provider')) && (
            <motion.div key="notif" variants={stepVariants} initial="initial" animate="animate" exit="exit" className="bg-slate-900/50 backdrop-blur-2xl border border-white/5 p-8 rounded-[40px] shadow-2xl">
              <div className="mb-8">
                <div className="flex justify-between items-center mb-6"><span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Step {step} of {totalSteps}</span><div className="flex gap-1">{[...Array(totalSteps)].map((_, i) => <div key={i} className={`h-1 rounded-full transition-all ${i < step ? 'w-4 bg-brand-pink' : 'w-2 bg-white/10'}`} />)}</div></div>
                <h2 className="text-2xl font-black text-white">Notifications</h2>
                <p className="text-slate-400 text-sm">Stay updated on messages and requests.</p>
              </div>
              <div className="space-y-4">
                {[
                  { id: 'push', label: 'Push Notifications', icon: Zap },
                  { id: 'email', label: 'Email Notifications', icon: Bell }
                ].map(o => (
                  <button key={o.id} onClick={() => updateForm({ notifications: { ...form.notifications, [o.id]: !form.notifications[o.id] } })} className="w-full flex items-center justify-between p-6 rounded-3xl bg-slate-800 border border-white/5 transition-all text-left">
                    <div className="flex items-center gap-4"><div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${form.notifications[o.id] ? 'bg-brand-pink/20 text-brand-pink' : 'bg-slate-900 text-slate-600'}`}><o.icon size={24} /></div><h4 className="font-bold text-white">{o.label}</h4></div>
                    <CheckCircle2 className={`h-6 w-6 transition-all ${form.notifications[o.id] ? 'text-green-500 scale-110' : 'text-slate-700 scale-100'}`} />
                  </button>
                ))}
                <div className="flex gap-4 pt-6">
                  <button onClick={handleBack} className="p-4 rounded-2xl bg-white/5 border border-white/5 text-slate-400 hover:text-white"><ArrowLeft size={24} /></button>
                  <button onClick={handleNext} className="flex-1 py-4 rounded-2xl bg-brand-pink text-white font-black hover:bg-brand-pink-dark shadow-xl shadow-brand-pink/20">Finish Setup</button>
                </div>
              </div>
            </motion.div>
          )}

          {/* FINAL STEP: Success */}
          {((step === 8 && role === 'user') || (step === 11 && role === 'provider')) && (
            <motion.div key="finish" variants={stepVariants} initial="initial" animate="animate" exit="exit" className="text-center space-y-8">
              <div className="relative inline-block">
                <div className="absolute inset-0 bg-green-500 blur-3xl opacity-20 animate-pulse" />
                <div className="relative w-32 h-32 rounded-[40px] bg-green-500 text-white flex items-center justify-center shadow-2xl shadow-green-500/40"><CheckCircle2 size={64} /></div>
              </div>
              <div>
                <h2 className="text-4xl font-black text-white mb-3">You're all set 🎉</h2>
                <p className="text-slate-400 max-w-sm mx-auto">{role === 'provider' ? "Your profile is submitted! Our team will verify your documents within 12-24 hours." : "Your profile is ready. Start exploring verified friends now."}</p>
              </div>
              {error && <div className="p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-bold">{error}</div>}
              <div className="grid gap-3 pt-4">
                <button onClick={handleFinish} disabled={saving} className="w-full py-5 rounded-3xl bg-white text-slate-900 font-black text-lg hover:bg-slate-200 transition-all shadow-xl flex items-center justify-center gap-3">{saving ? <Loader2 className="animate-spin" /> : (role === 'provider' ? 'Go to Dashboard' : 'Explore Friends')}{!saving && <ArrowRight />}</button>
                <button onClick={handleBack} className="text-slate-500 font-bold hover:text-white transition-all">Wait, I need to fix something</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default OnboardingPage;
